import { __awaiter } from "tslib";
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ReducerType } from '../types';
import { MISSING_CUSTOMER } from '@open-tender/types';
import { checkAuth, selectToken } from './account';
import { showNotification } from '../notifications';
const initialState = {
    entities: [],
    lookup: {},
    loading: 'idle',
    error: null
};
export var CustomerCommunicationPrefsActionType;
(function (CustomerCommunicationPrefsActionType) {
    CustomerCommunicationPrefsActionType["FetchCustomerCommunicationPreferences"] = "customer/fetchCustomerCommunicationPreferences";
    CustomerCommunicationPrefsActionType["AddCustomerCommunicationPreference"] = "customer/addCustomerCommunicationPreference";
    CustomerCommunicationPrefsActionType["RemoveCustomerCommunicationPreference"] = "customer/removeCustomerCommunicationPreference";
    CustomerCommunicationPrefsActionType["SetCustomerCommunicationDefaultPreferences"] = "customer/setCustomerCommunicationDefaultPreferences";
})(CustomerCommunicationPrefsActionType || (CustomerCommunicationPrefsActionType = {}));
export const fetchCustomerCommunicationPreferences = createAsyncThunk(CustomerCommunicationPrefsActionType.FetchCustomerCommunicationPreferences, (_, { dispatch, getState, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const api = getState().config.api;
        const token = selectToken(getState());
        if (!token)
            throw new Error(MISSING_CUSTOMER);
        const { data: communicationPreferences } = yield api.getCustomerCommunicationPreferences(token);
        return communicationPreferences;
    }
    catch (err) {
        const error = err;
        return checkAuth(error, dispatch, () => rejectWithValue(error));
    }
}));
export const addCustomerCommunicationPreference = createAsyncThunk(CustomerCommunicationPrefsActionType.AddCustomerCommunicationPreference, (requestData, { dispatch, getState, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const api = getState().config.api;
        const token = selectToken(getState());
        if (!token)
            throw new Error(MISSING_CUSTOMER);
        const data = {
            notification_area: requestData.area,
            notification_channel: requestData.channel
        };
        yield api.postCustomerCommunicationPreference(token, data);
        const { data: communicationPreferences } = yield api.getCustomerCommunicationPreferences(token);
        dispatch(showNotification('Preference added!'));
        if (requestData.callback)
            requestData.callback();
        return communicationPreferences;
    }
    catch (err) {
        const error = err;
        return checkAuth(error, dispatch, () => rejectWithValue(error));
    }
}));
export const removeCustomerCommunicationPreference = createAsyncThunk(CustomerCommunicationPrefsActionType.RemoveCustomerCommunicationPreference, (requestData, { dispatch, getState, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const api = getState().config.api;
        const token = selectToken(getState());
        if (!token)
            throw new Error(MISSING_CUSTOMER);
        yield api.deleteCustomerCommunicationPreference(token, requestData.prefId);
        const { data: communicationPreferences } = yield api.getCustomerCommunicationPreferences(token);
        dispatch(showNotification('Preference removed!'));
        if (requestData.callback)
            requestData.callback();
        return communicationPreferences;
    }
    catch (err) {
        const error = err;
        return checkAuth(error, dispatch, () => rejectWithValue(error));
    }
}));
export const setCustomerCommunicationDefaultPreferences = createAsyncThunk(CustomerCommunicationPrefsActionType.SetCustomerCommunicationDefaultPreferences, (requestData, { dispatch, getState, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const api = getState().config.api;
        const token = selectToken(getState());
        if (!token)
            throw new Error(MISSING_CUSTOMER);
        yield api.putCustomerCommunicationPreference(token, requestData.prefs);
        const { data: communicationPreferences } = yield api.getCustomerCommunicationPreferences(token);
        if (requestData.callback)
            requestData.callback();
        return communicationPreferences;
    }
    catch (err) {
        const error = err;
        return checkAuth(error, dispatch, () => rejectWithValue(error));
    }
}));
const customerCommunicationPrefsSlice = createSlice({
    name: ReducerType.CommunicationPrefs,
    initialState,
    reducers: {
        resetCustomerCommunicationPreferences: () => initialState,
        resetCustomerCommunicationPreferencesError: state => {
            state.error = null;
            state.loading = 'idle';
        },
        setCustomerCommunicationPreferences: (state, action) => {
            state.entities = action.payload;
            state.error = null;
        }
    },
    extraReducers: builder => {
        builder
            .addCase(fetchCustomerCommunicationPreferences.fulfilled, (state, action) => {
            state.entities = action.payload;
            state.loading = 'idle';
            state.error = null;
        })
            .addCase(fetchCustomerCommunicationPreferences.pending, state => {
            state.loading = 'pending';
        })
            .addCase(fetchCustomerCommunicationPreferences.rejected, (state, action) => {
            state.error = action.payload;
            state.loading = 'idle';
        })
            .addCase(addCustomerCommunicationPreference.fulfilled, (state, action) => {
            state.entities = action.payload;
            state.loading = 'idle';
            state.error = null;
        })
            .addCase(addCustomerCommunicationPreference.pending, state => {
            state.loading = 'pending';
        })
            .addCase(addCustomerCommunicationPreference.rejected, (state, action) => {
            state.error = action.payload;
            state.loading = 'idle';
        })
            .addCase(removeCustomerCommunicationPreference.fulfilled, (state, action) => {
            state.entities = action.payload;
            state.loading = 'idle';
            state.error = null;
        })
            .addCase(removeCustomerCommunicationPreference.pending, state => {
            state.loading = 'pending';
        })
            .addCase(removeCustomerCommunicationPreference.rejected, (state, action) => {
            state.error = action.payload;
            state.loading = 'idle';
        })
            .addCase(setCustomerCommunicationDefaultPreferences.fulfilled, (state, action) => {
            state.entities = action.payload;
            state.loading = 'idle';
            state.error = null;
        })
            .addCase(setCustomerCommunicationDefaultPreferences.pending, state => {
            state.loading = 'pending';
        })
            .addCase(setCustomerCommunicationDefaultPreferences.rejected, (state, action) => {
            state.error = action.payload;
            state.loading = 'idle';
        });
    }
});
export const { resetCustomerCommunicationPreferences, resetCustomerCommunicationPreferencesError, setCustomerCommunicationPreferences } = customerCommunicationPrefsSlice.actions;
export const customerCommunicationPrefsReducer = customerCommunicationPrefsSlice.reducer;
export const selectCustomerCommunicationPreferences = (state) => state.customer.communicationPreferences;
